import type { Key } from '../i18n/strings';

/*
    The single source of truth for every visual value in Iris Mail.

    Nothing may hard-code a colour, radius or font size. The theme store writes these
    onto the document root as CSS custom properties at runtime, which is what lets the
    settings screen edit them live (see design/turn-2 — the prototype proves the same
    contract with style.setProperty / removeProperty).

    Values are verbatim from C:/dev/haktan-src/src/styles/tokens.css, extended with the
    app-level tokens the Claude Design bundle introduced.
*/

export type ThemeName = 'dark' | 'light';
export type Tokens = Record<string, string>;

/*
    summail.

    The product's own identity, taken from the brand pack rather than invented here:
    `~/projects/summail-android/brand/README.md` is the source, and the five colours below are its five
    colours. The concept is an envelope with Makrosum's Σ on an orange seal — post, and the seal
    saying "sent, verified".

    | #F26A21 | Makrosum Turuncu | the seal, the accent          |
    | #C8500F | Kiremit          | primary on light (4.56:1 ✓AA) |
    | #1C1F26 | Mürekkep         | envelope outline, text, surface|
    | #FDE4D2 | Şeftali          | secondary container            |
    | #EDECEA | Krem             | neutral ground                 |

    ⚠ The orange is NOT a background for white text — 3.06:1, it fails AA. The brand pack says so
    and the two themes obey it in opposite directions: light fills a button with kiremit and
    writes white on it, dark fills with orange and writes ink. That is why the palette lives in
    the themes rather than in a brand override — a single set of colours cannot do both.

    Only tints and shades of these five are derived (darker grounds for the dark theme, a lighter
    orange for hover). Nothing new is invented.
*/
export const DARK: Tokens = {
    '--bg': '#14171c',
    '--surface': '#1c1f26',
    '--surface2': '#181b21',
    '--border': 'rgba(237, 236, 234, 0.09)',
    '--border2': 'rgba(237, 236, 234, 0.14)',

    '--text': '#edecea',
    '--text-dim': 'rgba(237, 236, 234, 0.62)',
    /*
        Bu iki basamak ölçülerek yükseltildi. Eski değerlerde (0.5 / 0.36) listedeki 10 piksellik
        tarih yazısı zemine karşı 3.00:1 çıkıyordu; WCAG AA 4.5 istiyor ve axe bunu ciddi bulgu
        olarak veriyordu. Yeni değerler en kötü yüzeyde bile 5.2 ve 4.77.

        İki basamağın arası daralmış olması kabul edilmiş bir bedel: 10 pikselde 4.5:1'in altına
        inmeden birbirinden ayrık İKİ gri kalmıyor. Ayrımı asıl taşıyan zaten punto ve ağırlık.
    */
    '--text-faint': 'rgba(237, 236, 234, 0.56)',
    '--text-mute': 'rgba(237, 236, 234, 0.52)',

    '--accent': '#f26a21',
    '--accent-strong': '#ff8442',
    // Ink on orange, per the brand pack: white here would be 3.06:1 and fail.
    '--on-accent': '#1c1f26',
    '--accent-bg': 'rgba(242, 106, 33, 0.16)',

    '--green': '#4ade80',
    '--green-bg': 'rgba(74, 222, 128, 0.12)',
    '--amber': '#fbbf24',
    '--amber-bg': 'rgba(251, 191, 36, 0.09)',
    '--red': '#f87171',
    '--red-bg': 'rgba(248, 113, 113, 0.09)',

    '--chip-bg': 'rgba(237, 236, 234, 0.06)',
    '--header-bg': 'rgba(20, 23, 28, 0.92)',
    '--overlay': 'rgba(10, 12, 15, 0.55)',
    '--hover': 'rgba(237, 236, 234, 0.05)',
    /*
        Açık olan satırın zemini NÖTR, işaretli satırınki (--accent-bg) vurgu rengi.

        Eskiden ikisi de vurgu tintiydi ve pratikte aynı sayıydı — koyuda 0.15'e karşı 0.16,
        açık temada ikisi de birebir #fde4d2. Yani "hangisini okuyorum" ile "hangilerini
        seçtim" ekranda ayırt edilemiyordu. Vurgu, üzerinde bir işlem yapılacak olana ait
        (toplu seçim); okunmakta olan satır bir işlem değil, bir konum. Outlook ve Gmail de
        böyle ayırıyor.
    */
    '--sel': 'rgba(237, 236, 234, 0.10)',
    '--card-shadow': 'none',
};

export const LIGHT: Tokens = {
    '--bg': '#edecea',
    '--surface': '#ffffff',
    '--surface2': '#ffffff',
    '--border': 'rgba(28, 31, 38, 0.12)',
    '--border2': 'rgba(28, 31, 38, 0.2)',

    '--text': '#1c1f26',
    '--text-dim': 'rgba(28, 31, 38, 0.68)',
    /*
        Koyu temadaki ile aynı sebep, aynı ölçüm: eski değerlerde (0.55 / 0.45) en kötü yüzeyde
        2.65:1'e kadar düşüyordu. Açıkta sıkışma daha da belirgin — beyaz zeminde 4.5:1 ile siyah
        arasında üç ayrık gri için yer yok. Basamaklar duruyor ama araları dar.
    */
    '--text-faint': 'rgba(28, 31, 38, 0.67)',
    '--text-mute': 'rgba(28, 31, 38, 0.66)',

    // Kiremit, not the orange: the brand pack measured it at 4.56:1 on white, and the orange at
    // 3.06:1. This is the one that may carry white text.
    '--accent': '#c8500f',
    '--accent-strong': '#a83f08',
    '--on-accent': '#ffffff',
    '--accent-bg': '#fde4d2',

    '--green': '#2f9e5f',
    '--green-bg': '#e5f6ec',
    '--amber': '#b7791f',
    '--amber-bg': '#fdf4e3',
    '--red': '#c53030',
    '--red-bg': '#fdecea',

    '--chip-bg': 'rgba(28, 31, 38, 0.06)',
    '--header-bg': 'rgba(237, 236, 234, 0.92)',
    '--overlay': 'rgba(28, 31, 38, 0.35)',
    '--hover': 'rgba(28, 31, 38, 0.04)',
    '--sel': 'rgba(28, 31, 38, 0.08)',
    '--card-shadow': '0 2px 10px rgba(28, 31, 38, 0.07)',
};

export const THEMES: Record<ThemeName, Tokens> = { dark: DARK, light: LIGHT };

export const FONTS: Tokens = {
    '--font-sans': "'Space Grotesk', system-ui, sans-serif",
    '--font-mono': "'JetBrains Mono', ui-monospace, monospace",
};

export type RadiusKey = '--r-card' | '--r-input' | '--r-btn' | '--r-badge';

export const RADII: { key: RadiusKey; name: string; value: number }[] = [
    { key: '--r-card', name: 'r-card', value: 14 },
    { key: '--r-input', name: 'r-input', value: 12 },
    { key: '--r-btn', name: 'r-btn', value: 10 },
    { key: '--r-badge', name: 'r-badge', value: 6 },
];

export const FIXED_SHAPE: Tokens = {
    '--r-pill': '999px',
    '--r-modal': '16px',
};

// The groups the settings token editor renders. Order is the order shown.
export const TOKEN_GROUPS: { title: Key; keys: string[] }[] = [
    { title: 'tokens.surfaces', keys: ['--bg', '--surface', '--surface2', '--border'] },
    { title: 'tokens.text', keys: ['--text', '--text-dim', '--text-mute'] },
    { title: 'tokens.accent', keys: ['--accent', '--accent-strong', '--accent-bg'] },
    { title: 'tokens.semantic', keys: ['--green', '--amber', '--red'] },
];

export type Density = 'comfortable' | 'cosy' | 'compact';

type DensitySpec = {
    padY: number;
    padX: number;
    avatar: number;
    gap: number;
    /* font sizes are baseSize + these offsets, so the base-size slider moves everything */
    sender: number;
    subject: number;
    snippet: number;
    snippetLines: number;
};

export const DENSITY: Record<Density, DensitySpec> = {
    // Avatar 34 değil 32: artık seçim hedefi de o, ama şerit soldan yer aldığı için satırın
    // toplam genişliği aynı kalsın diye iki piksel geri verildi (arayüz v2, 2. adım).
    comfortable: { padY: 10, padX: 12, avatar: 32, gap: 11, sender: -0.5, subject: -1, snippet: -1.5, snippetLines: 2 },
    cosy: { padY: 7, padX: 11, avatar: 28, gap: 10, sender: -1, subject: -1.5, snippet: -2, snippetLines: 1 },
    compact: { padY: 5, padX: 10, avatar: 22, gap: 9, sender: -1.5, subject: -2, snippet: -2, snippetLines: 0 },
};

/*
    Taban punto 14'ten 15'e çıktı (2026-09-09) ve bu ölçeğin yeniden çapalanmasıyla aynı işin
    ikinci yarısı: ölçek basamakları tabana yaklaştırıldı, taban da bir adım yukarı alındı.
    Kaydırıcı 11..20 arasında; eski yoğunluğu isteyen bir adım aşağı iner ve her şey birlikte
    küçülür — çünkü hiçbir yere sabit piksel yazılmıyor.
*/
export const BASE_SIZE_DEFAULT = 15;

/*
    The type scale.

    Before this there were sixteen distinct font sizes in the interface — 9, 9.5, 10, 10.5, 11,
    11.5, 12, 12.5, 13, 13.5, 14, 14.5, 15, 18, 19, 22 — each written as a number where it was
    needed. Sixteen sizes is not a scale, it is an accident, and it has a cost beyond tidiness:
    nothing could be retuned. "Make the interface a little roomier" meant finding and judging a
    hundred numbers.

    Seven steps, and each existing size maps to the nearest one, so nothing moves by more than
    half a pixel. That is deliberate: a design system earns its keep by being adopted, and one
    that visibly rearranges the screen on the day it lands gets reverted.

    Expressed as offsets from the base size, like the row tokens above, so the base-size slider
    moves the whole interface rather than the message list alone. Sizes written as literals would
    have been dead to it.
*/
type Step = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/*
    The scale was re-anchored on 2026-09-09, and the reason is a measurement rather than a taste.

    It used to put the workhorse BELOW the base: `sm` was 12.5 while the base was 14, and the base
    itself was reserved for headings. Counted across the app, 193 of 242 type declarations sat at
    13px or under and only 15 were at the base or above — that is, the interface was almost
    entirely rendered smaller than its own reading size. Somebody trying to follow a setup guide
    on the sign-in screen said it plainly: hard to read.

    So the steps did not change their RELATIONSHIPS, they moved up together and the two most-used
    ones were pulled closer to the base. `sm` — 105 uses, the text people actually read — now
    lands exactly on 14. Nothing that carries prose sits under it; only badges and mono kickers,
    which are glanced at rather than read, stay smaller.

    The base is still what the slider in Settings moves, so anybody who wants the old density can
    take it back down one step.
*/
const SCALE: Record<Step, number> = {
    '2xs': -4, // 11 — mono kickers, badges: glanced at, not read
    xs: -2.5, // 12.5 — secondary labels
    sm: -1, // 14 — the workhorse, most body text in the chrome
    md: -0.5, // 14.5 — buttons, field text
    lg: 0, // 15 — headings inside panes
    xl: 4, // 19 — screen titles
    '2xl': 8, // 23 — the one place a number is the message
};

export function typeTokens(base: number): Tokens {
    const out: Tokens = {};
    for (const [step, offset] of Object.entries(SCALE)) {
        out[`--fs-${step}`] = `${base + offset}px`;
    }
    return out;
}

/*
    The spacing scale.

    Same story as the type: 108 separate padding, margin and gap declarations, drawn from eleven
    different numbers. These are tokens so they can be retuned together; the values are the ones
    already in use, rounded onto a ladder.
*/
export const SPACING: Tokens = {
    '--sp-1': '2px',
    '--sp-2': '4px',
    '--sp-3': '6px',
    '--sp-4': '8px',
    '--sp-5': '10px',
    '--sp-6': '12px',
    '--sp-7': '16px',
    '--sp-8': '20px',
};

/*
    Motion.

    There were two transitions in the whole interface, and neither asked whether the reader wanted
    them. Some people are made ill by movement on screen, and the browser has carried that answer
    for years — the app simply never read it.

    Durations are tokens so that honouring the preference is one decision in one place rather than
    a media query per animated thing. Reduced motion sets them to zero: the state still changes,
    it just arrives without the journey.
*/
export function motionTokens(reduced: boolean): Tokens {
    return {
        '--motion-fast': reduced ? '0ms' : '120ms',
        '--motion-base': reduced ? '0ms' : '160ms',
        '--motion-slow': reduced ? '0ms' : '240ms',
    };
}

/*
    Dokunma hedefinin asgari ölçüsü.

    Parmak fareyle aynı isabeti tutturamaz: 30x30 bir düğme imleçle kolay, dokunmayla ıskalanır.
    Masaüstünde her düğmeyi 44'e büyütmek ise arayüzü şişirir — bu yüzden ölçü koşullu: fareyle
    kullanılan cihazda 0 kalır, yani `[data-hit]` kuralı orada hiçbir şey yapmaz (global.css).

    Tetik ekran genişliği değil, `any-pointer: coarse`: parmakla kullanılan bir cihaz geniş
    ekranda da parmakla kullanılır (dokunmatik dizüstü, tablet), dar pencereye çekilmiş bir
    masaüstü ise hâlâ faredir. `pointer` yerine `any-pointer`: birincil işaretleyicisi fare olan
    dokunmatik dizüstünde de parmak kullanılabiliyor.
*/
export function hitTokens(coarse: boolean): Tokens {
    return { '--hit': coarse ? '44px' : '0px' };
}

export function densityTokens(density: Density, base: number): Tokens {
    const d = DENSITY[density];
    return {
        '--row-pad-y': `${d.padY}px`,
        '--row-pad-x': `${d.padX}px`,
        '--row-avatar': `${d.avatar}px`,
        '--row-gap': `${d.gap}px`,
        '--fs-base': `${base}px`,
        '--fs-sender': `${base + d.sender}px`,
        '--fs-subject': `${base + d.subject}px`,
        '--fs-snippet': `${base + d.snippet}px`,
        '--snippet-lines': String(d.snippetLines),
    };
}

/*
    Avatar palette — initials avatars at every density was an explicit decision.

    Işıklıklar 0.60 civarından 0.08 aşağı çekildi. Sebep ölçüm: beyaz baş harfler eski renklerin
    ÜÇÜNDE 4.04–4.26:1 veriyordu, WCAG AA 4.5 istiyor. Renkler arasındaki el ayarı korunsun diye
    hepsi eşit miktarda düştü; yeni en düşük oran 4.56.
*/
export const AVATAR_COLORS = [
    'oklch(0.52 0.13 285)',
    'oklch(0.52 0.13 205)',
    'oklch(0.50 0.13 150)',
    'oklch(0.54 0.13 60)',
    'oklch(0.52 0.14 25)',
    'oklch(0.52 0.13 330)',
    'oklch(0.50 0.12 250)',
    'oklch(0.52 0.12 100)',
];
